import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import toast from "react-hot-toast";

import {
  useGetBenefitsQuery,
  useGetBenefitPartnersQuery,
  useGetBenefitCategoriesQuery,
  useCreateBenefitFeaturedMutation,
} from "../../store/services/benefits";
import { useDebounce } from "../../hooks/useDebounce";
import { Benefit } from "../../models/benefits";
import { DateRangeFilter } from "../../components/Filters/DateRangeFilter";
import { AppDialog, StatusBadge, emptyMessage } from "../../components/ui";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";

interface AddFeaturedBenefitDialogProps {
  visible: boolean;
  onHide: () => void;
  /** ids ya presentes en la lista curada (activos o pausados) para bloquear duplicados. */
  existingBenefitIds: string[];
}

/**
 * DataTable memoiza cada celda comparando `rowData` (ver `cellMemo` en la doc de
 * PrimeReact): un cambio en closures externas al row (como `existingBenefitIds`)
 * NO alcanza para que la celda se re-renderice. Por eso el estado "ya destacado"
 * se mezcla directamente en el objeto de la fila.
 */
type BenefitRow = Benefit & { isFeatured: boolean };

const SEARCH_DEBOUNCE_MS = 500;

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const AddFeaturedBenefitDialog = ({
  visible,
  onHide,
  existingBenefitIds,
}: AddFeaturedBenefitDialogProps) => {
  const [name, setName] = React.useState("");
  const [partnerId, setPartnerId] = React.useState<string | null>(null);
  const [categoryId, setCategoryId] = React.useState<string | null>(null);
  const [dateFrom, setDateFrom] = React.useState<Date | null>(getToday);
  const [dateTo, setDateTo] = React.useState<Date | null>(null);
  const [addingBenefitId, setAddingBenefitId] = React.useState<string | null>(null);
  // La lista curada (y por lo tanto `existingBenefitIds`) se actualiza recién
  // cuando el refetch disparado por la invalidación de tags vuelve del server;
  // hasta entonces esta copia local evita que la fila quede mostrando "Agregar"
  // tras un alta exitosa.
  const [justAddedIds, setJustAddedIds] = React.useState<Set<string>>(new Set());

  const { data: partners } = useGetBenefitPartnersQuery({ tenantId: null });
  const { data: categories } = useGetBenefitCategoriesQuery({ tenantId: null });

  // Se debouncean todos los filtros juntos para que la búsqueda dispare sola
  // mientras el usuario escribe/selecciona, sin necesitar salir del campo.
  const filters = React.useMemo(
    () => ({ name, partnerId, categoryId, dateFrom, dateTo }),
    [name, partnerId, categoryId, dateFrom, dateTo]
  );
  const debouncedFilters = useDebounce(filters, SEARCH_DEBOUNCE_MS);

  // partnerId/categoryId van al servidor (soportados por /bo/benefits); nombre
  // y rango de fechas no tienen filtro server-side todavía, así que se acotan
  // en cliente, igual que hace la pantalla principal de beneficios.
  const { data: benefits, isLoading, isFetching } = useGetBenefitsQuery(
    {
      partnerId: debouncedFilters.partnerId ?? undefined,
      categoryId: debouncedFilters.categoryId ?? undefined,
    },
    { skip: !visible }
  );

  const partnerNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    (partners ?? []).forEach((partner) => map.set(partner.id, partner.name ?? partner.id));
    return map;
  }, [partners]);

  const filteredBenefits = React.useMemo(() => {
    const term = debouncedFilters.name.trim().toLowerCase();
    const { dateFrom: from, dateTo: to } = debouncedFilters;
    return (benefits ?? []).filter((benefit) => {
      if (term && !(benefit.title ?? "").toLowerCase().includes(term)) return false;
      if (from && benefit.endDate && new Date(benefit.endDate) < from) return false;
      if (to && benefit.startDate && new Date(benefit.startDate) > to) return false;
      return true;
    });
  }, [benefits, debouncedFilters]);

  // `isFeatured` viaja dentro de la propia fila (no como closure externa) para
  // que el cellMemo de PrimeReact detecte el cambio y re-renderice esa celda.
  const rows: BenefitRow[] = React.useMemo(
    () =>
      filteredBenefits.map((benefit) => ({
        ...benefit,
        isFeatured: existingBenefitIds.includes(benefit.id) || justAddedIds.has(benefit.id),
      })),
    [filteredBenefits, existingBenefitIds, justAddedIds]
  );

  const [addBenefit, { isLoading: isAdding }] = useCreateBenefitFeaturedMutation();

  const handleAdd = (benefit: Benefit) => {
    setAddingBenefitId(benefit.id);
    addBenefit({ benefitId: benefit.id })
      .unwrap()
      .then(() => {
        setJustAddedIds((prev) => new Set(prev).add(benefit.id));
        toast.success(`"${benefit.title}" agregado a destacados`);
      })
      .catch((err) => {
        toast.error(getAdminFacingApiErrorMessage(err, "No se pudo agregar el beneficio"));
      })
      .finally(() => setAddingBenefitId(null));
  };

  const resetFilters = () => {
    setName("");
    setPartnerId(null);
    setCategoryId(null);
    setDateFrom(getToday());
    setDateTo(null);
  };

  const hasFilters = Boolean(name || partnerId || categoryId || dateTo);

  const handleHide = () => {
    resetFilters();
    setJustAddedIds(new Set());
    onHide();
  };

  const partnerBodyTemplate = (benefit: BenefitRow) =>
    partnerNameById.get(benefit.partnerId) ?? "-";

  const validityBodyTemplate = (benefit: BenefitRow) => {
    const start = benefit.startDate
      ? new Date(benefit.startDate).toLocaleDateString("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : null;
    const end = benefit.endDate
      ? new Date(benefit.endDate).toLocaleDateString("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : null;
    if (start && end) return `${start} - ${end}`;
    if (start) return `Desde ${start}`;
    if (end) return `Hasta ${end}`;
    return "-";
  };

  const actionsBodyTemplate = (benefit: BenefitRow) =>
    benefit.isFeatured ? (
      <StatusBadge label="Ya destacado" tone="success" />
    ) : (
      // `+` circular con relleno de marca, igual que el resto de los `+`.
      <Button
        icon="pi pi-plus"
        rounded
        size="small"
        aria-label="Agregar a destacados"
        tooltip="Agregar a destacados"
        tooltipOptions={{ position: "top" }}
        onClick={() => handleAdd(benefit)}
        loading={isAdding && addingBenefitId === benefit.id}
      />
    );

  return (
    <AppDialog
      variant="detail"
      visible={visible}
      onHide={handleHide}
      header="Agregar beneficio destacado"
      style={{ width: "75rem", maxWidth: "95vw" }}
    >
      {/* Mismo patrón que los filtros del listado de beneficios (§ INC-02). */}
      <div className="grid mt-3 row-gap-3 mb-3">
        <div className="col-12 md:col-4">
          <FloatLabel className="w-full">
            <InputText
              id="filterName"
              className="w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <label htmlFor="filterName">Nombre</label>
          </FloatLabel>
        </div>
        <div className="col-12 md:col-4">
          <FloatLabel className="w-full">
            <Dropdown
              inputId="filterPartner"
              className="w-full"
              value={partnerId}
              options={partners ?? []}
              optionLabel="name"
              optionValue="id"
              showClear
              onChange={(e) => setPartnerId(e.value ?? null)}
            />
            <label htmlFor="filterPartner">Partner</label>
          </FloatLabel>
        </div>
        <div className="col-12 md:col-4">
          <FloatLabel className="w-full">
            <Dropdown
              inputId="filterCategory"
              className="w-full"
              value={categoryId}
              options={categories ?? []}
              optionLabel="name"
              optionValue="id"
              showClear
              onChange={(e) => setCategoryId(e.value ?? null)}
            />
            <label htmlFor="filterCategory">Categoría</label>
          </FloatLabel>
        </div>
        <DateRangeFilter
          variant="filter"
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          startDateId="featuredBenefitFrom"
          endDateId="featuredBenefitTo"
          labelFrom="Vigencia desde"
          labelTo="Vigencia hasta"
          columnClassName="col-12 md:col-4"
        />
        <div className="col-12 md:col-4 flex justify-content-end align-items-center">
          <Button
            type="button"
            label="Limpiar filtros"
            icon="pi pi-filter-slash"
            severity="secondary"
            outlined
            size="small"
            onClick={resetFilters}
          />
        </div>
      </div>

      <DataTable
        value={rows}
        loading={isLoading || isFetching}
        dataKey="id"
        emptyMessage={emptyMessage("beneficios", hasFilters)}
        paginator
        rows={10}
      >
        <Column field="title" header="Título" style={{ minWidth: "14rem" }} />
        <Column header="Partner" body={partnerBodyTemplate} style={{ minWidth: "10rem" }} />
        <Column header="Vigencia" body={validityBodyTemplate} style={{ width: "10rem" }} />
        <Column
          header=""
          body={actionsBodyTemplate}
          style={{ width: "10rem" }}
          exportable={false}
        />
      </DataTable>
    </AppDialog>
  );
};
